// SETUP-REGROUP (review #18, founder 2026-10-21): the one list of Setup's pages, grouped the way
// Linear and Notion group their settings — Company, People & access, Structure, Measurement,
// Finance, Workforce, Integrations, Security & audit — each page with a one-line description. The
// Setup sidebar, the Setup home (`/c/{slug}/setup`, its search), ⌘K's Setup pages, the top
// bar's crumb and the description line under every Setup page's header all read it, so a page
// is named and explained once. Personal pages (Account & security, Appearance, Notifications,
// My subscriptions) are not company Setup: they are `YOUR_SETTINGS`, under the account menu.
import {
  ArmchairIcon,
  ArrowDownToLineIcon,
  ArrowLeftRightIcon,
  AudioLinesIcon,
  BanknoteIcon,
  BellIcon,
  BlocksIcon,
  BookOpenIcon,
  BotIcon,
  BrainIcon,
  BriefcaseIcon,
  Building2Icon,
  ClipboardCheckIcon,
  CpuIcon,
  CreditCardIcon,
  EyeIcon,
  FileTextIcon,
  GaugeIcon,
  IdCardIcon,
  KeyRoundIcon,
  LayersIcon,
  LayoutTemplateIcon,
  LibraryIcon,
  ListChecksIcon,
  type LucideIcon,
  NetworkIcon,
  PackageIcon,
  PiggyBankIcon,
  PlugZapIcon,
  PlusIcon,
  ReceiptIcon,
  RepeatIcon,
  RocketIcon,
  RulerIcon,
  ScaleIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  StethoscopeIcon,
  SunMoonIcon,
  TagIcon,
  TargetIcon,
  TerminalIcon,
  UserRoundCogIcon,
  UsersIcon,
  UsersRoundIcon,
  WalletIcon,
  WaypointsIcon,
  WebhookIcon,
} from "lucide-react";

export type SetupGroupKey =
  | "company"
  | "people"
  | "structure"
  | "measurement"
  | "finance"
  | "agents"
  | "integrations"
  | "security";

export type SetupGroup = {
  key: SetupGroupKey;
  label: string;
  description: string;
  icon: LucideIcon;
};

export const SETUP_GROUPS: readonly SetupGroup[] = [
  {
    key: "company",
    label: "Company",
    description: "The company's profile, calendar and what it pays for.",
    icon: Building2Icon,
  },
  {
    key: "people",
    label: "People & access",
    description: "Who is in the company, what each person may open, and the teams they form.",
    icon: UsersIcon,
  },
  {
    key: "structure",
    label: "Structure",
    description: "The org chart: seats, the job roles they are for, and templates.",
    icon: NetworkIcon,
  },
  {
    key: "measurement",
    label: "Measurement",
    description: "What is measured, the targets, and the weekly report.",
    icon: RulerIcon,
  },
  {
    key: "finance",
    label: "Finance",
    description: "Costs, prices, pay and revenue behind the chart's money views.",
    icon: WalletIcon,
  },
  {
    // UX-A 0.1: "Agents" means AGs only; this group is the Workforce's setup (DEs and AGs).
    key: "agents",
    label: "Workforce",
    description:
      "Digital employees and agents: creating them, budgets, skills, deploys and models.",
    icon: BotIcon,
  },
  {
    key: "integrations",
    label: "Integrations",
    description: "Keys, tool connections, imports, webhooks and memory.",
    icon: PlugZapIcon,
  },
  {
    key: "security",
    label: "Security & audit",
    description: "Outside access to the company and its log.",
    icon: ShieldCheckIcon,
  },
];

export type SetupEntry = {
  /** Stable key (⌘K, tests). */
  key: string;
  /** The route; under `/c/$slug/setup/…` unless `mode` says otherwise. */
  to: string;
  /** An anchor on the page (`to` stays the route). */
  hash?: string;
  /** Search params the link carries (a tab of the page). */
  search?: Record<string, string>;
  /** Another name for a part of a listed page: only the Setup search and ⌘K find it. */
  alias?: boolean;
  label: string;
  /** One line, shown under the page's header, on the Setup home and in ⌘K. */
  description: string;
  icon: LucideIcon;
  group: SetupGroupKey;
  /** The add-on the page needs; absent while it is off. */
  feature?: "kpi" | "memory";
  /** False: the home and ⌘K list it, the sidebar does not (an anchor on a Setup page). */
  sidebar?: boolean;
  /**
   * The page lives in another mode (UX-A 0.8): only the Setup search and ⌘K find it, marked ↗;
   * the home's cards and the sidebar list Setup pages only.
   */
  mode?: "agents" | "kpis" | "reports";
  /** Extra words the search matches (old names, synonyms). */
  keywords?: string;
};

/** The mode a catalog row in another mode opens, as its ↗ mark names it. */
export const MODE_LABEL: Record<NonNullable<SetupEntry["mode"]>, string> = {
  agents: "Workforce",
  kpis: "KPIs",
  reports: "Reports",
};

export const SETUP_ENTRIES: readonly SetupEntry[] = [
  // Company
  {
    key: "general",
    to: "/c/$slug/setup/general",
    label: "General",
    description:
      "Name, currency, time zone, week start and fiscal year; Friday report and cost rates.",
    icon: Building2Icon,
    group: "company",
    keywords: "profile company name timezone calendar",
  },
  // WOS-BUNDLE: company tags.
  {
    key: "tags",
    to: "/c/$slug/setup/tags",
    label: "Tags",
    description: "The company's task tags: rename, recolour, merge or delete them.",
    icon: TagIcon,
    group: "company",
    keywords: "labels task tags merge colour",
  },
  {
    key: "addons",
    to: "/c/$slug/setup/addons",
    label: "Add-ons",
    description: "Which add-ons the company has, what each depends on and what it costs.",
    icon: BlocksIcon,
    group: "company",
    keywords: "entitlements features",
  },
  // WALLET-P1 §6.1 + BILLING-PLANS §6.1: one Billing page — the prepaid AI credit, then the plan,
  // agents' maintenance and invoices (Setup's roles: owners, admins, agency operators).
  {
    key: "billing",
    to: "/c/$slug/setup/billing",
    label: "Billing",
    description: "Your credit, plan, agents' maintenance and invoices.",
    icon: CreditCardIcon,
    group: "company",
    keywords:
      "credit wallet balance top-up funds add funds spend invoice receipt plan invoices quotes payment card subscription",
  },
  // People & access
  {
    key: "people",
    to: "/c/$slug/setup/people",
    label: "People",
    description:
      "Invite people and set each person's access: Owner, Admin, Member or Client viewer.",
    icon: UsersIcon,
    group: "people",
    keywords: "invite invitations members access owner admin deactivate",
  },
  {
    key: "teams",
    to: "/c/$slug/setup/teams",
    label: "Teams",
    description: "Groups of seats with a lead; each team gets its own space and channel.",
    icon: UsersRoundIcon,
    group: "people",
  },
  {
    // KPI-VISIBILITY (05 §KPI visibility): grants that widen whose KPI data a role, seat, team or
    // person sees, with a "Preview as…" tree.
    key: "kpi-visibility",
    to: "/c/$slug/setup/kpi-visibility",
    label: "KPI visibility",
    description:
      "Who sees whose KPIs: everyone their own seat and below; grants add their level or up.",
    icon: EyeIcon,
    group: "people",
    keywords: "permissions kpi access levels peers up chain everything preview grants",
  },
  // Structure
  {
    key: "seats",
    to: "/c/$slug/setup/seats",
    label: "Seats",
    description:
      "The chart's seats: who holds each, its responsibilities, recurring work and KPIs.",
    icon: ArmchairIcon,
    group: "structure",
    keywords: "org chart positions holders",
  },
  {
    key: "roles",
    to: "/c/$slug/setup/roles",
    label: "Job roles",
    description: "What a seat is for (Recruiter, Sourcer) and the metrics a new seat starts with.",
    icon: IdCardIcon,
    group: "structure",
    keywords: "roles role templates",
  },
  {
    key: "chart",
    to: "/c/$slug/setup/chart",
    label: "Chart",
    description: "The org chart's lenses and who sees each, score bands, weights and card details.",
    icon: LayersIcon,
    group: "structure",
    keywords: "lenses bands weights org chart settings",
  },
  {
    key: "templates",
    to: "/c/$slug/setup/templates",
    label: "Templates",
    description: "Preview and apply a template's seats, spaces, boards and starter tasks.",
    icon: LayoutTemplateIcon,
    group: "structure",
  },
  {
    // M8-ASSESS: the team X-Ray rounds, their reports and the chart changes they propose.
    key: "assessments",
    to: "/c/$slug/setup/assessments",
    label: "Assessments",
    description:
      "Ask the team about their tasks, hours and energy; read the report, shape the chart.",
    icon: ClipboardCheckIcon,
    group: "structure",
    keywords: "x-ray assessment survey questionnaire round",
  },
  {
    // COLLECT-FLOWS: each person's collection session and what each role still waits for.
    key: "collection",
    to: "/c/$slug/setup/collection",
    label: "Collection",
    description: "What each person still owes their agents, and what each role waits for.",
    icon: ListChecksIcon,
    group: "structure",
    keywords: "collect knowledge readiness onboarding session questions role readiness",
  },
  // Measurement
  {
    key: "metrics",
    to: "/c/$slug/setup/metrics",
    label: "Metrics & targets",
    description: "The metric catalog, and each seat's targets and weights.",
    icon: RulerIcon,
    group: "measurement",
    feature: "kpi",
    keywords: "kpi kpis scorecard measurables",
  },
  {
    // AGENT-REPORTING (13 §Reporting duties): who reports each seat KPI and when, the review gate,
    // and what a seat's agent may do with its KPIs.
    key: "kpi-reporting",
    to: "/c/$slug/setup/kpi-reporting",
    label: "KPI reporting",
    description: "Who reports each KPI and when, reminders, late notices and the review gate.",
    icon: GaugeIcon,
    group: "measurement",
    feature: "kpi",
    keywords: "reporting duties duty schedule due late reminders review agent kpi scopes",
  },
  {
    key: "friday-report",
    to: "/c/$slug/setup/general",
    hash: "friday-report",
    label: "Friday report",
    description: "When the weekly report is generated, whether it sends itself, and to whom.",
    icon: FileTextIcon,
    group: "measurement",
    sidebar: false,
    keywords: "reports recipients weekly scorecard",
  },
  {
    key: "priorities",
    to: "/c/$slug/kpis/priorities",
    label: "Priorities",
    description: "The quarter's priorities are set in KPIs › Priorities.",
    icon: TargetIcon,
    group: "measurement",
    sidebar: false,
    mode: "kpis",
    keywords: "rocks keystones goals quarter",
  },
  {
    // KPIS-MODE (2026-10-22): data entry lives in KPIs' sidebar.
    key: "kpis-data-entry",
    to: "/c/$slug/kpis/import",
    label: "KPI data entry",
    description: "Import a CSV, enter hours or numbers in KPIs › Data entry.",
    icon: RulerIcon,
    group: "measurement",
    sidebar: false,
    mode: "kpis",
    keywords: "csv import upload hours numbers enter data entry scorecard grid",
  },
  // Finance
  {
    // KPIS-MODE (2026-10-22): the finance views moved from Chart to Reports › Finance.
    key: "finance-reports",
    to: "/c/$slug/kpis/reports/finance",
    label: "Finance reports",
    description: "Budget vs actual, money per seat, forecast, scenarios, headcount, exports.",
    icon: WalletIcon,
    group: "finance",
    sidebar: false,
    mode: "reports",
    keywords: "budget actual margins forecast scenarios headcount exports money",
  },
  {
    key: "costs",
    to: "/c/$slug/setup/costs",
    label: "Costs",
    description: "The month's cost ledger: add a cost by hand or reverse one.",
    icon: ReceiptIcon,
    group: "finance",
  },
  {
    key: "finance-access",
    to: "/c/$slug/setup/finance",
    label: "Finance access",
    description: "Who is a finance admin, and who may see a seat's pay.",
    icon: ShieldCheckIcon,
    group: "finance",
  },
  {
    key: "finance-prices",
    to: "/c/$slug/setup/finance/prices",
    label: "Prices",
    description: "What the company charges, per client, service or seat.",
    icon: TagIcon,
    group: "finance",
  },
  {
    key: "finance-fx",
    to: "/c/$slug/setup/finance/fx",
    label: "FX rates",
    description: "Exchange rates into the reporting currency.",
    icon: ArrowLeftRightIcon,
    group: "finance",
    keywords: "currency exchange",
  },
  {
    key: "finance-compensation",
    to: "/c/$slug/setup/finance/compensation",
    label: "Compensation",
    description: "What each seat's holder is paid.",
    icon: BanknoteIcon,
    group: "finance",
    keywords: "salary pay",
  },
  {
    key: "finance-expenses",
    to: "/c/$slug/setup/finance/expenses",
    label: "Recurring costs",
    description: "Costs that repeat every month, on a seat or the company.",
    icon: RepeatIcon,
    group: "finance",
    keywords: "expenses",
  },
  {
    key: "finance-clients",
    to: "/c/$slug/setup/finance/clients",
    label: "Clients & revenue",
    description: "The clients and the revenue booked against them.",
    icon: BriefcaseIcon,
    group: "finance",
  },
  {
    key: "finance-categories",
    to: "/c/$slug/setup/finance/categories",
    label: "Categories",
    description: "The chart of accounts costs and revenue are booked to.",
    icon: BookOpenIcon,
    group: "finance",
  },
  // Workforce
  {
    key: "de-new",
    to: "/c/$slug/setup/des/new",
    label: "Add digital employee",
    description: "One flow: a seat, the job, KPIs, standing orders, budget and chat.",
    icon: PlusIcon,
    group: "agents",
    keywords: "de create hire new digital employee deploy an agent",
  },
  {
    key: "subscriptions",
    to: "/c/$slug/setup/subscriptions",
    label: "DE subscriptions",
    description: "Each digital employee's monthly fee.",
    icon: CreditCardIcon,
    group: "agents",
  },
  {
    key: "deploys",
    to: "/c/$slug/setup/deploys",
    label: "Deploys",
    description: "Every deploy into the company; approve a version's first deploy.",
    icon: RocketIcon,
    group: "agents",
  },
  {
    key: "de-overrides",
    to: "/c/$slug/setup/de-overrides",
    label: "Standing orders & models",
    description: "The company's standing orders and models for a DE, used from its next deploy.",
    icon: SlidersHorizontalIcon,
    group: "agents",
  },
  {
    key: "model-policy",
    to: "/c/$slug/setup/model-policy",
    label: "Model policy",
    description: "Which models agents may use, the default per task, and own AI keys.",
    icon: CpuIcon,
    group: "agents",
    keywords: "llm models ai keys",
  },
  {
    // B5-DECIDE: the cheap classifier DEs call to sort many items, and their rubrics.
    key: "decisions",
    to: "/c/$slug/setup/decisions",
    label: "Decisions",
    description: "The classifier DEs use to sort items: Jev, the board gate and their rubrics.",
    icon: ScaleIcon,
    group: "agents",
    keywords: "decide classifier jev openrouter rubric triage board gate",
  },
  {
    // COS-SLICE1: the Chief of Staff's loop, quiet hours, caps and each DE's digest.
    key: "chief-of-staff",
    to: "/c/$slug/setup/chief-of-staff",
    label: "Chief of Staff",
    description: "How often the Chief of Staff checks in, its caps, quiet hours and digests.",
    icon: UserRoundCogIcon,
    group: "agents",
    keywords: "cos digest loop interval quiet hours follow-up cards suggestions mode solo team",
  },
  {
    // COS-MODES: a person's own card (every member opens it; also under the account menu).
    key: "agent-direction",
    to: "/c/$slug/setup/agent-direction",
    label: "Who may direct my agents",
    description: "Hands-off, Tell me or Ask me first when another agent directs yours.",
    icon: WaypointsIcon,
    group: "agents",
    keywords: "chief of staff team mode take back ask me first tell me hands-off",
  },
  {
    // VOICE-CHOICES: the company default voice (managers) and each person's own voice and mood.
    key: "voice",
    to: "/c/$slug/setup/voice",
    label: "Voice",
    description: "The company's default voice; each person picks their own voice and mood.",
    icon: AudioLinesIcon,
    group: "agents",
    keywords: "voice live workspace speech aiden ryan mood default price per minute",
  },
  {
    // WF-CONTROL: the Workforce Control add-on and the caller's own Claude Code connections.
    key: "claude-code",
    to: "/c/$slug/setup/claude-code",
    label: "Claude Code connection",
    description: "Workforce Control from Claude Code: the add-on and your connections.",
    icon: TerminalIcon,
    group: "agents",
    keywords: "workforce control plugin mcp claude code token pairing",
  },
  // UX pass 1.1: Budgets and Skill packs moved here from the Workforce sidebar.
  {
    key: "budgets",
    to: "/c/$slug/setup/budgets",
    label: "Budgets",
    description: "What each level may spend on agent work, when it warns and where it stops.",
    icon: PiggyBankIcon,
    group: "agents",
    keywords: "spend cap limit cost alert threshold agent budget",
  },
  {
    key: "skill-packs",
    to: "/c/$slug/setup/skill-packs",
    label: "Skill packs",
    description: "Instructions digital employees can run, each pinned by its content hash.",
    icon: PackageIcon,
    group: "agents",
    keywords: "skills instructions packs roles",
  },
  // Integrations
  {
    key: "keys-connections",
    to: "/c/$slug/setup/connections",
    label: "Keys & connections",
    // CONNECTIONS-AREA: the page moved to Team › Connections; this entry opens it.
    description:
      "Now in Team › Connections: sign-in apps, API keys and AI model keys, saved logins, agent keys.",
    icon: KeyRoundIcon,
    group: "integrations",
    keywords: "api keys oauth subscription paths connections ai model keys accounts logins team",
  },
  {
    // UX-A 1.5: a tab of Keys & connections; found by search and ⌘K, not listed on its own.
    key: "connection-checks",
    to: "/c/$slug/setup/connections",
    search: { tab: "checks" },
    label: "Connection checks",
    description: "Each company connection's state: check it now, refresh or disconnect it.",
    icon: StethoscopeIcon,
    group: "integrations",
    alias: true,
    keywords: "health status connections",
  },
  {
    key: "data-import",
    to: "/c/$slug/setup/data-import",
    label: "Import",
    description:
      "Bring your history over from Zulip or ClickUp: preview, map, dry run, then import.",
    icon: ArrowDownToLineIcon,
    group: "integrations",
    keywords: "zulip clickup migrate migration export history import",
  },
  {
    key: "webhooks",
    to: "/c/$slug/setup/webhooks",
    label: "Webhooks",
    description: "Send the company's events to an outside URL.",
    icon: WebhookIcon,
    group: "integrations",
  },
  {
    key: "recall",
    to: "/c/$slug/setup/recall",
    label: "Recall",
    description: "How the company's memory reaches Recall.",
    icon: BrainIcon,
    group: "integrations",
    feature: "memory",
    keywords: "memory",
  },
  {
    key: "memory-privacy",
    to: "/c/$slug/setup/memory-privacy",
    label: "Memory & privacy",
    description: "Retention, legal holds, forgets and the memory audit.",
    icon: ShieldCheckIcon,
    group: "integrations",
    feature: "memory",
    keywords: "retention hold forget gdpr delete audit export",
  },
  {
    key: "memory",
    to: "/c/$slug/setup/memory",
    label: "Memory",
    description: "Review, browse and tend what the company's agents remember.",
    icon: LibraryIcon,
    group: "integrations",
    feature: "memory",
    keywords: "recall review browse spaces rules health charter",
  },
  // Security & audit
  {
    key: "break-glass",
    to: "/c/$slug/setup/break-glass",
    label: "Break-glass",
    description: "Agency access sessions: approve, end, and the log of what was read.",
    icon: ShieldAlertIcon,
    group: "security",
    keywords: "audit log agency access",
  },
];

/** The entries a company sees: those whose add-on is off are absent. */
export function setupEntries(features: { kpi: boolean; memory: boolean }): SetupEntry[] {
  return SETUP_ENTRIES.filter((e) => !e.feature || features[e.feature]);
}

/** A Setup page the home's cards list unsearched: not a page in another mode, not an alias. */
export function isListed(e: SetupEntry): boolean {
  return !e.mode && !e.alias;
}

/** Entries matching a search: every word in the label, description, group or keywords. */
export function matchSetup(entries: readonly SetupEntry[], query: string): SetupEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...entries];
  return entries.filter((e) => {
    const group = SETUP_GROUPS.find((g) => g.key === e.group)?.label ?? "";
    const text = `${e.label} ${e.description} ${group} ${e.keywords ?? ""}`.toLowerCase();
    return words.every((w) => text.includes(w));
  });
}

/** The page under `/c/{slug}/setup/…` (`rest` = the path after `setup/`), for crumbs and descriptions. */
export function setupPageOf(rest: string): { label: string; description?: string } | undefined {
  const path = rest.replace(/\/+$/, "");
  if (path === "") return undefined;
  const entry = SETUP_ENTRIES.find(
    (e) => !e.hash && isListed(e) && e.to === `/c/$slug/setup/${path}`,
  );
  if (entry) return { label: entry.label, description: entry.description };
  if (EXTRA_SETUP_PAGES[path]) return EXTRA_SETUP_PAGES[path];
  // UX-B: one item of a listed page (`budgets/{id}`, `skill-packs/{id}`) is named by its list.
  const parent = path.split("/").slice(0, -1).join("/");
  if (parent && ITEM_PAGES.includes(parent)) return { label: setupPageOf(parent)?.label ?? "" };
  return undefined;
}

/** Listed pages whose items have a page of their own under them. */
const ITEM_PAGES = ["budgets", "skill-packs"];

/** Setup pages reached from another page, not listed themselves. */
const EXTRA_SETUP_PAGES: Record<string, { label: string; description?: string }> = {};

/** The account menu's "Your settings": personal, never the company's. */
export type PersonalEntry = {
  key: string;
  to: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Inside a company only (the link carries its slug). */
  company?: boolean;
};

export const YOUR_SETTINGS: readonly PersonalEntry[] = [
  {
    key: "account",
    to: "/account",
    label: "Account & security",
    description: "Your name, password and signed-in devices.",
    icon: UserRoundCogIcon,
  },
  {
    key: "appearance",
    to: "/settings/appearance",
    label: "Appearance",
    description: "Theme and sidebar, saved on this device only.",
    icon: SunMoonIcon,
  },
  {
    // SEARCH-NOTIF's page: preferences are per company (the person's membership), so the row
    // carries the company's slug; `/settings/notifications` redirects to it (wave 11b).
    key: "notifications",
    to: "/c/$slug/notifications",
    label: "Notifications",
    description: "What reaches your Inbox and when: mentions, followed work, approvals.",
    icon: BellIcon,
    company: true,
  },
  {
    key: "my-subscriptions",
    to: "/c/$slug/subscriptions",
    label: "My subscriptions",
    description: "Your own AI keys, tool connections and channel subscriptions.",
    icon: KeyRoundIcon,
    company: true,
  },
  {
    // COS-MODES: the Setup page every member opens (Setup itself is managers only).
    key: "agent-direction",
    to: "/c/$slug/setup/agent-direction",
    label: "Who may direct my agents",
    description: "Hands-off, Tell me or Ask me first when another agent directs yours.",
    icon: WaypointsIcon,
    company: true,
  },
  {
    // VOICE-CHOICES: who you talk to in the Live Workspace, and in what mood.
    key: "voice",
    to: "/c/$slug/setup/voice",
    label: "Voice",
    description: "Who you talk to in the Live Workspace, its mood and its price per minute.",
    icon: AudioLinesIcon,
    company: true,
  },
];

/** The description of a personal page by its path (`/account`, `/c/acme/subscriptions`). */
export function personalPageOf(pathname: string): PersonalEntry | undefined {
  const path = pathname.replace(/\/+$/, "");
  return YOUR_SETTINGS.find((e) =>
    e.company ? path.replace(/^\/c\/[^/]+/, "/c/$slug") === e.to : path === e.to,
  );
}
